import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';

const EvidencePanel = ({ attachments, onAddAttachment, onRemoveAttachment, readOnly = false }) => {
  const [dragOver, setDragOver] = useState(false);

  const mockFiles = attachments || [];

  const getFileIcon = (name) => {
    const ext = name?.split('.')?.pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp']?.includes(ext)) return { icon: 'Image', color: 'text-blue-500' };
    if (['pdf']?.includes(ext)) return { icon: 'FileText', color: 'text-red-500' };
    if (['xlsx', 'xls', 'csv']?.includes(ext)) return { icon: 'Table', color: 'text-emerald-500' };
    if (['docx', 'doc']?.includes(ext)) return { icon: 'FileText', color: 'text-blue-600' };
    return { icon: 'File', color: 'text-muted-foreground' };
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const handleFileInput = (e) => {
    const files = Array.from(e?.target?.files || []);
    files?.forEach(file => {
      onAddAttachment?.({
        id: Date.now() + Math.random(),
        name: file?.name,
        size: file?.size,
        type: file?.type,
        uploadedBy: 'Current User',
        uploadedAt: new Date()?.toISOString(),
        url: URL.createObjectURL(file),
      });
    });
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    files?.forEach(file => {
      onAddAttachment?.({
        id: Date.now() + Math.random(),
        name: file?.name,
        size: file?.size,
        type: file?.type,
        uploadedBy: 'Current User',
        uploadedAt: new Date()?.toISOString(),
        url: URL.createObjectURL(file),
      });
    });
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Icon name="Paperclip" size={18} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Evidence & Attachments</h3>
            <p className="text-xs text-muted-foreground">{mockFiles?.length} file{mockFiles?.length !== 1 ? 's' : ''} attached</p>
          </div>
        </div>
      </div>

      {!readOnly && (
        <div
          onDragOver={e => { e?.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`mb-4 border-2 border-dashed rounded-xl p-6 text-center transition-all ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-muted/30'
          }`}
        >
          <Icon name="Upload" size={24} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground mb-1">Drop files here or click to upload</p>
          <p className="text-xs text-muted-foreground mb-3">Images, PDFs, documents — max 50MB each</p>
          <label className="cursor-pointer px-4 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors">
            Browse Files
            <input type="file" multiple className="hidden" onChange={handleFileInput} accept="image/*,.pdf,.doc,.docx,.xlsx,.xls,.csv" />
          </label>
        </div>
      )}

      {mockFiles?.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground">
          <Icon name="FolderOpen" size={28} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No evidence files attached yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {mockFiles?.map(file => {
            const { icon, color } = getFileIcon(file?.name);
            return (
              <div key={file?.id} className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg hover:bg-muted/60 transition-colors group">
                <Icon name={icon} size={20} className={color} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{file?.name}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span>{formatSize(file?.size)}</span>
                    {file?.uploadedBy && <span>by {file?.uploadedBy}</span>}
                    {file?.uploadedAt && <span>{format(new Date(file.uploadedAt), 'MMM dd, yyyy')}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {file?.url && (
                    <a href={file?.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-foreground transition-colors">
                      <Icon name="ExternalLink" size={14} />
                    </a>
                  )}
                  {!readOnly && (
                    <button onClick={() => onRemoveAttachment?.(file?.id)} className="p-1.5 rounded hover:bg-red-100 hover:text-red-600 text-muted-foreground transition-colors">
                      <Icon name="Trash2" size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EvidencePanel;
